import PropTypes from 'prop-types';
import React, { Component } from 'react';
import BaseElement from '../../BaseElement/BaseElement';
import omit from 'lodash/omit';
import assign from 'lodash/assign';
import tinycolor from 'tinycolor2';
import RichTextElement from '../../RichTextElement/RichTextElement';
import RichTextElementProps from '../../RichTextElement/RichTextElementProps';
import { setDesktopRefById } from 'crazyEditor/core/elements/utils/elementRefUtils';
import {findDOMNode} from 'react-dom';
import _ from 'lodash';
import {BORDER_RADIUS_PROP_NAMES} from 'crazyEditor/core/constants/panelConstants';
import {buildInteractiveElementsList} from 'crazyEditor/core/elements/utils/commonElementMethods';
import InlineStyle from 'crazyEditor/components/Elements/common/InlineStyle';

require('./GlobalStyles/HorizontalTabsElement.scss');

const DEFAULT_TAB_TEXT_COLOR = '#222222';
const DEFAULT_TAB_BORDER = {borderWidth: 1, borderStyle: 'solid', borderColor: '#D3D3CD'};

const classNamePrefix = 'cl-htabs-el';

function getPrefix(className) {
  return `${classNamePrefix}-${className}`;
}

function getCSS(uId, numItems, selectedBackgroundColor, selectedTextColor, borderWidth) {
  const CSS = [];
  for (let i = 1; i <= numItems; i++) {
    const tabsId = `${classNamePrefix}-tabs${i}-${uId}`;
    CSS.push(`
        #${tabsId}:checked ~ div .${classNamePrefix}-label${i}-${uId} {
          color: ${selectedTextColor} !important;
          background-color: ${selectedBackgroundColor} !important;
          border-bottom: 0px!important;
        }
        #${tabsId}:checked ~ div .${classNamePrefix}-label${i}-${uId} .label-content-connector {
          display: block!important;
          height: ${borderWidth}px;
          position: relative;
          width: 100%;
        }

        #${tabsId} .${classNamePrefix}-thin-bottom {
          border-bottom-width: ${Math.max(borderWidth - 1, 1)}px!important;
        }

        #${tabsId}:checked ~ div .${classNamePrefix}-content${i}-${uId} {
          opacity: 1;
          z-index: 1;
        }
      `);
  }

  return CSS.join('').replace(/\s{2,}/g, '');
}

export default class HorizontalTabsElement extends Component {
  constructor(props) {
    super(props);
    const { children } = props.data;
    this.selectedTabId = children[0].id;
  }

  componentWillMount() {
    this.uniqueId = `u_${this.props.id}`;
  }

  componentDidMount = (prevProps) => {
    const tabsDOMElement = findDOMNode(this.refs.tabElement);
    const contentChildDOMElement = findDOMNode(this.refs.contentChild);
    if (tabsDOMElement && contentChildDOMElement) {
      this.childrenOffset = contentChildDOMElement.getBoundingClientRect().top - tabsDOMElement.getBoundingClientRect().top;
    }
    this.refreshInteractiveElementsList(null);
    const {children} = this.props.data;
    if (_.get(prevProps, 'data.children.length') !== children.length) {
      if (!this.selectedTabId) {
        this.refs.input0.checked = true;
        this.selectedTabId = children[0].id;
      }
    }
  };

  componentDidUpdate = _.throttle(this.componentDidMount, 500);

  // get styles of elements that have a cursor: pointer. in tabs those are the tab headers
  getInteractiveElements = () => {
    return this.interactiveElements;
  };

  getChildrenOffset = () => {
    return this.childrenOffset;
  };

  getSelectedTabId = () => {
    return this.selectedTabId;
  };

  getElementContent = (tabNames, children, layout, tabStyles, tabChildContainerStyle, selectedBackgroundColor) => {
    const labels = [];
    const inputs = [];
    const sections = [];
    const labelsShadow = [];

    _.forEach(tabNames, (tabName, index) => {
      const ind = index + 1;
      const tabChild = children[index];
      const tabsId = getPrefix(`tabs${ind}-${this.uniqueId}`);
      const marginLeft = index > 0 ? -tabStyles.borderWidth : 0;
      labelsShadow.push(
        <div // this is for shadow
          className={[getPrefix('label'), getPrefix(`label${ind}-${this.uniqueId}`)].join(' ')}
          style={{...tabStyles, zIndex: 0, marginLeft}}
          ref="labelOffsetPlaceholder"
          key={`shadow${ind}`}
        >
          {tabName.replace('&amp;', '&')}
        </div>);
      labels.push(
        <label key={`label${ind}`} htmlFor={tabsId}
               data-interactive="true"
               data-active-element-id={tabChild.id}
               className={[getPrefix('label'), getPrefix(`label${ind}-${this.uniqueId}`), getPrefix('thin-bottom')].join(' ')}
               style={{...tabStyles, marginLeft, boxShadow: null}}>{tabName.replace('&amp;', '&')}
          <div className="label-content-connector" style={{display: 'none'}}/>
        </label>);
      inputs.push(<input ref={`input${index}`} key={`input${ind}`} id={tabsId} type="radio" name={getPrefix(`tabs-${this.uniqueId}`)} value={ind} defaultChecked={ind === 1}/>);
      sections.push(<div
        key={`section${ind}`}
        className={[getPrefix('content'), getPrefix(`content${ind}-${this.uniqueId}`), index === 0 ? 'phantomjs-relative' : ''].join(' ')}
      >
        {tabChild && React.createElement(RichTextElement.elementClass, {
          ...tabChild,
          style: {height: '100%', width: '100%', top: 0, position: 'relative'},
          definitionProps: RichTextElementProps,
          ref: setDesktopRefById.bind(null, tabChild.id),
          key: `tabChild${ind}`,
          className: getPrefix('child-element'),
        })}
      </div>);
    });
    const {textAlign} = tabStyles;
    return (
      <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
        {inputs}
        <div style={{width: '100%', textAlign, flexShrink: 0}}>
          <span>
            {labelsShadow}
          </span>
        </div>
        <div style={{position: 'absolute', top: 0, left: 0, zIndex: 1, width: '100%', textAlign}}>
          <span>
            {labels}
          </span>
        </div>
        <div
          style={{
            ...tabChildContainerStyle,
            backgroundColor: selectedBackgroundColor,
            height: 'calc(100% - 70px)', // workaround for phantomjs issue
            marginTop: -tabStyles.borderWidth,
          }}
          ref="contentChild"
          className={getPrefix('content-container')}
        >
        {sections}
        </div>
      </div>
    );
  };

  updateSelectedTab() {
    const {children} = this.props.data;
    this.selectedTabId = null;
    for (let i = 0; i < children.length; i++) {
      if (this.refs[`input${i}`].checked) {
        this.selectedTabId = children[i].id;
        break;
      }
    }
  }

  refreshInteractiveElementsList = (onComplete) => {
    this.updateSelectedTab();
    let interactiveElements = [];
    const wrapper = findDOMNode(this.refs.tabElement);
    if (wrapper) {
      interactiveElements = buildInteractiveElementsList(wrapper);
      if (interactiveElements.length > 0) {
        this.interactiveElements = interactiveElements;
        if (onComplete) {
          onComplete();
        }
      }
    }
  };

  render() {
    const { tabNames, children, compProps, childrenProps } = this.props.data;
    const { layout } = this.props;
    const containerCompProps = _.merge(
      _.zipObject(BORDER_RADIUS_PROP_NAMES, _.times(BORDER_RADIUS_PROP_NAMES.length, _.constant(4))), // set default border radius if not defied
      omit(this.props.compProps, ['color'])
    );

    const selectedBackgroundColor = _.get(childrenProps, 'compProps.backgroundColor', 'white');
    let selectedTextColor = containerCompProps && containerCompProps.backgroundColor || DEFAULT_TAB_TEXT_COLOR;

    const tinySelectedBackgroundColor = tinycolor(selectedBackgroundColor);
    const tinySelectedTextColor = tinycolor(selectedTextColor);
    if (tinySelectedBackgroundColor.isDark() && tinySelectedTextColor.isDark()) {
      selectedTextColor = tinySelectedTextColor.brighten(70).toHexString();
    } else if (tinySelectedBackgroundColor.isLight() && tinySelectedTextColor.isLight()) {
      selectedTextColor = tinySelectedTextColor.darken(60).toHexString();
    }
    const tabStyles = omit(assign({}, DEFAULT_TAB_BORDER, compProps, _.omit(containerCompProps, ['textAlign'])), ['zIndex', 'borderBottomLeftRadius', 'borderBottomRightRadius']);
    let borderRadiusStyleOverride;
    switch (tabStyles.textAlign) {
      default:
      case 'left':
        borderRadiusStyleOverride = {borderTopLeftRadius: 0};
        break;
      case 'right':
        borderRadiusStyleOverride = {borderTopRightRadius: 0};
        break;
      case 'center':
        break;
    }
    const tabChildContainerStyle = {
      // top: -(parseInt(tabStyles.borderWidth, 10) || 1),
      ..._.pick(tabStyles, ['border', 'borderWidth', 'borderStyle', 'borderColor', 'boxShadow']),
      ..._.pick(containerCompProps, BORDER_RADIUS_PROP_NAMES),
      ...borderRadiusStyleOverride
    };
    return (
      <BaseElement {...omit(this.props, 'compProps')} compProps={{zIndex: _.get(this.props, 'compProps.zIndex')}} ref="tabElement">
        <InlineStyle css={getCSS(this.uniqueId, tabNames.length, selectedBackgroundColor, selectedTextColor, tabStyles.borderWidth || 1)}/>
        <div className={getPrefix('wrapper')} style={{height: '100%'}}>
          {this.getElementContent(tabNames, children, layout, tabStyles, tabChildContainerStyle, selectedBackgroundColor)}
        </div>
      </BaseElement>
    );
  }
}

HorizontalTabsElement.propTypes = {
  id: PropTypes.string.isRequired,
  layout: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  })
};